export type DomainInput = {
  purchasePrice: number;
  loanAmount?: number | null;
  equityIntoPurchase?: number | null;
  purchaseCostRate: number;
  currentRentSq: number;
  targetRentZm: number;
  ownerCostsMonthly: number;
  recurringCapexMonthly: number;
  rentalColdRentMonthly: number;
  rentalNonRecoverableCostsMonthly: number;
  rentalMaintenanceMonthly: number;
  rentalVacancyRate: number;
  rentalIncomeTaxRate: number;
  allowNegativeRentalTaxOffset: boolean;
  rentalBuildingValueShare: number;
  rentalDepreciationRate: number;
  selfUseAreaM2: number;
  rentalAreaM2: number;
  netElwCashflowMonthly: number;
  elwColdRentMonthly: number;
  elwVacancyRate: number;
  elwNonRecoverableCostsMonthly: number;
  elwMaintenanceMonthly: number;
  elwTaxRate: number;
  elwLegallyRentable: boolean;
  elwLegalRentabilityHaircut: number;
  separateEntrance: boolean;
  legalLivingArea: boolean;
  roomHeightOk: boolean;
  rescueRouteOk: boolean;
  separateMetersOrAllocation: boolean;
  ownParking: boolean;
  landValueShare: number;
  buildingValueShare: number;
  rentalBuildingShare: number;
  rentalDebtInterestAllocationMethod: RentalDebtInterestAllocationMethod;
  rentalDebtInterestShare: number;
  rentalAllocatedLoanAmount: number;
  livingAreaM2: number;
  energyCertificateAreaM2: number;
  currentHeatingCostMonthly: number;
  currentCoolingKwhPerActiveMonth: number;
  currentCoolingActiveMonths: number;
  candidateEnergyDemandKwhM2a: number;
  heatDemandKwhM2a: number;
  heatPumpJaz: number;
  auxiliaryElectricityKwhYear: number;
  candidateEnergyPriceEurKwh: number;
  candidateEnergySource: EnergySource;
  candidateCoolingProfile: CoolingProfile;
  electricityPriceEurKwh: number;
  pvBenefitMonthly: number;
  pvPhases: PvPhase[];
  energyCostGrowthRate: number;
  co2PriceEurTon: number;
  co2PriceGrowthRate: number;
  etfReturnRate: number;
  etfTaxRate: number;
  inflationRate: number;
  savingsReturnRate: number;
  fcfGrowthRate: number;
  rentGrowthRate: number;
  ownerCostsGrowthRate: number;
  recurringCapexGrowthRate: number;
  savingsCapacityGrowthRate: number;
  propertyAppreciationRate: number;
  availableEquity: number;
  existingDepotMarketValue: number;
  existingDepotUnrealizedGain: number;
  minimumReserve: number;
  cashReserveTarget: number;
  cashReserveInitial: number | null;
  cashReserveReturnRate: number | null;
  loanInterestRate: number;
  initialRepaymentRate: number;
  comparisonInitialRepaymentRate: number;
  financingRatio: number | null;
  financingRatioDefinition: FinancingRatioDefinition;
  maintainFinancingRatioDuringPriceSearch: boolean;
  existingDepotWithdrawalForPurchase: number;
  savingsBeforeHousingMonthly: number;
  retirementYear: number;
  postRetirementSavingsBeforeHousingMonthly: number;
  minimumBuyPathSavingsMonthly: number;
  horizonYears: number;
  immediateCapex: number;
  futureCapex: number;
  futureCapexYear: number;
  futureCapexDurationYears: number;
  capexValueEffectRate: number;
  renovationPhases: RenovationPhase[];
  poolAnnualCost: number;
  poolFutureCapex: number;
  poolFutureCapexYear: number;
  saunaFutureCapex: number;
  saunaFutureCapexYear: number;
  pvBatteryReplacementYear: number;
  pvBatteryReplacementCost: number;
  inverterReplacementYear: number;
  inverterReplacementCost: number;
  heatPumpReplacementYear: number;
  heatPumpReplacementCost: number;
  ventilationReplacementYear: number;
  ventilationReplacementCost: number;
  kitchenFurnitureDepreciationMonthly: number;
  sellingCostRate: number;
  fixedRateYears: number;
  refinanceInterestRate: number;
  refinanceStressRate: number;
  refinanceRepaymentRate: number;
  mixedUseSelfUseYears: number;
  searchMinPrice: number;
  searchMaxPrice: number;
};

export type EnergySource =
  | 'gas'
  | 'oil'
  | 'district_heat'
  | 'heat_pump'
  | 'electricity'
  | 'pellets'
  | 'unknown';

export type FinancingRatioDefinition =
  | 'purchase_price_ltv'
  | 'all_in_financing_ratio';

export type CoolingProfile = 'better' | 'similar' | 'worse';
export type RentalDebtInterestAllocationMethod = 'area' | 'loan' | 'manual';
export type RenovationFundingSource =
  | 'income'
  | 'cash_reserve'
  | 'etf_sidecar'
  | 'renovation_loan'
  | 'confirmed_grant';

export type RenovationPhase = {
  name: string;
  startYear: number;
  durationYears: number;
  capex: number;
  capexValueEffectRate: number;
  energyDemandAfter: number | null;
  energySourceAfter: EnergySource | null;
  ownerCostsDeltaMonthly: number;
  recurringCapexDeltaMonthly: number;
  fundingSource: RenovationFundingSource;
  confirmedGrantAmount: number;
};

export type PvPhase = {
  startMonth: number;
  endMonth: number | null;
  monthlyBenefit: number;
  growthRate: number;
};

export type Decision = 'inspect' | 'negotiate' | 'weak-positive' | 'reject';

export type AsymptoticLimitStatus =
  | 'defined'
  | 'defined_non_robust'
  | 'property_matches_benchmark'
  | 'property_outgrows_benchmark'
  | 'cashflow_limit_undefined'
  | 'loan_not_finitely_amortized'
  | 'invalid_reference_base'
  | 'infeasible_strategy'
  | 'not_applicable';

export type DomainOutput = {
  assumptions: DomainInput;
  warnings: string[];
  decisionSummary: DecisionSummary;
  priceGuidance: PriceGuidance;
  comparisonRentYield: ComparisonRentYield;
  housingChangePremiumMonthly: number;
  rentEquivalent30Monthly: number | null;
  rentEquivalent50Monthly: number | null;
  housingChangePremiumIntrinsicEtf: number;
  housingChangePremiumIntrinsicTg: number;
  benchmarkPremiumSq: number;
  benchmarkPremiumZm: number;
  capexHaircutEtf: number;
  capexHaircutTg: number;
  partialRental: PartialRentalResult;
  wealthDeltaVsCurrentRentEtf: number;
  wealthDeltaVsCurrentRentEtfContext: WealthDeltaContext;
  requiredNetElwCashflowForSqEtfNeutrality: number | null;
  repaymentComparison: RepaymentComparison;
  views: ViewResult[];
  rentalViews: RentalViewResult[];
  mixedUseViews: MixedUseViewResult[];
};

export type PriceGuidance = {
  offerPrice: number;
  standaloneIntrinsicPrice: number;
  debtServiceSupportedPrice: number | null;
  etfNeutralPrice: number | null;
  hardMaxPrice: number | null;
  targetPurchasePrice: number | null;
  requiredDiscountRate: number | null;
};

export type ComparisonRentYield = {
  comparisonColdRentMonthly: number;
  netOperatingIncomeMonthly: number;
  totalPurchaseCost: number;
  grossYieldOnTotalCost: number;
  netYieldOnTotalCost: number;
};

export type DecisionSummary = {
  primaryView: string;
  classification:
    | 'reject'
    | 'weak_positive'
    | 'fragile_negative'
    | 'near_break_even'
    | 'fragile_positive'
    | 'robust_positive';
  statusQuoClassification:
    | 'reject'
    | 'weak_positive'
    | 'fragile_negative'
    | 'near_break_even'
    | 'fragile_positive'
    | 'robust_positive';
  priceMargin: number | null;
  priceMarginPercent: number | null;
  standaloneMargin: number;
  standaloneMarginPercent: number;
  savingsRateMargin: number;
  mainRisk: string;
  nextAction: string;
};

export type ViewResult = {
  scenario: 'sq' | 'zm';
  benchmark: 'etf' | 'tg';
  key: string;
  comparisonRent: number;
  freeCashFlowMonthly: number;
  currentEnergyCostMonthly: number;
  candidateEnergyCostMonthly: number;
  energySavingsMonthly: number;
  energyAdjustedComparisonRent: number;
  cashFlowBridge: CashFlowBridge;
  rentPathSavingsMonthly: number;
  buyPathSavingsMonthly: number;
  monthlyPayment: number;
  initialLoan: number;
  equityInvested: number;
  liquidReserveAfterPurchase: number;
  financingRatioDefinition: FinancingRatioDefinition;
  requestedFinancingRatio: number | null;
  allInInitialCost: number;
  loanToPurchasePriceRatio: number;
  loanToAllInCostRatio: number;
  cashReserveInitial: number;
  cashReserveReturnRate: number;
  etfSidecarInitial: number;
  etfSidecarTerminalValue: number;
  existingDepotWithdrawalGross: number;
  existingDepotWithdrawalNet: number;
  fundingGap: number;
  liquidityShortfall: number;
  financingFeasible: boolean;
  maintainFinancingRatioDuringPriceSearch: boolean;
  cashReserveAtHorizon: number;
  investmentAccountAtHorizon: number;
  loanPayoffMonth: number | null;
  loanTotalInterest: number;
  intrinsicValue: number;
  intrinsicMethod: string;
  maximumLeveragedPrice: number | null;
  standaloneIntrinsicValue: number;
  leveragedWealthBreakEvenPrice: number | null;
  totalStrategyValue: number;
  savingsRateCap: number | null;
  debtResidualCap: number | null;
  offerPrice: number;
  intrinsicValuePremium: number | null;
  leveragedMaxDistance: number | null;
  objectValueIndex: number | null;
  strategyIndex: number | null;
  leverageConsumption: number | null;
  rentWealthAtHorizon: number;
  buyWealthAtHorizon: number;
  wealthDeltaAtHorizon: number;
  portfolioContext: PortfolioContext;
  wealthDeltaContext: WealthDeltaContext;
  remainingDebtAtHorizon: number;
  minimumBuyPathSavingsMonthly: number;
  terminalPropertyEquity: number;
  remainingDebtYear5: number;
  remainingDebtYear10: number;
  remainingDebtYear30: number;
  remainingDebtYear50: number;
  asymptoticFamilyWealthLossRate: number | null;
  asymptoticFamilyWealthRetentionRate: number | null;
  asymptoticWealthDragCapitalAtPurchase: number | null;
  asymptoticBenchmarkCapitalAtPurchase: number | null;
  initialFinancialCapitalDiversion: number;
  ongoingCashflowDragPresentValue: number | null;
  asymptoticPropertyEquityContribution: number | null;
  asymptoticLimitStatus: AsymptoticLimitStatus;
  asymptoticLimitRobust: boolean;
  finiteFamilyWealthLossRate: number | null;
  yearsTo90PercentConvergence: number | null;
  yearsTo95PercentConvergence: number | null;
  mortgagePaymentDragPv: number | null;
  ownerCostDragPv: number | null;
  maintenanceAndCapexDragPv: number | null;
  energyBenefitPv: number | null;
  avoidedRentBenefitPv: number | null;
  elwNetBenefitPv: number | null;
  pvElectricityBenefitPv: number | null;
  taxEffectPv: number | null;
  postPayoffHousingBenefitPv: number | null;
  decision: Decision;
};

export type RepaymentStrategyResult = {
  initialRepaymentRate: number;
  initialContractualPayment: number;
  payoffMonth: number | null;
  totalInterest: number;
  totalPayments: number;
  investmentAccountAtHorizon: number;
  cashReserveAtHorizon: number;
  remainingDebtAtHorizon: number;
  terminalPropertyEquity: number;
  netWealthAtHorizon: number;
};

export type RepaymentComparison = {
  cashFlowPolicy: string;
  cashReserveTarget: number;
  baseStrategy: RepaymentStrategyResult;
  comparisonStrategy: RepaymentStrategyResult;
  investmentAccountDifference: number;
  cashReserveDifference: number;
  terminalPropertyEquityDifference: number;
  additionalInterest: number;
  netWealthDifference: number;
  bridgeReconciledNetWealthDifference: number;
  netWealthDifferenceContext: WealthDeltaContext;
  breakEvenEtfReturnRate: number | null;
};

export type CashFlowBridge = {
  comparisonRent: number;
  energySavingsMonthly: number;
  energyAdjustedComparisonRent: number;
  ownerCostsMonthly: number;
  recurringCapexMonthly: number;
  lifestyleCostsMonthly: number;
  partialRentalCashflowAfterTaxMonthly: number;
  freeCashFlowMonthly: number;
};

export type PartialRentalResult = {
  enabled: boolean;
  selfUseAreaM2: number;
  rentalAreaM2: number;
  rentalAreaShare: number;
  effectiveRentMonthly: number;
  selfUseCashflow: number;
  rentalCashflowBeforeTax: number;
  rentalTaxableIncome: number;
  rentalTaxEffect: number;
  rentalCashflowAfterTax: number;
  combinedBuyPathSavingsMonthly: number;
  debtInterestAllocatedMonthly: number;
  depreciationMonthly: number;
  legalRentabilityFactor: number;
  legalCriteria: {
    elwLegallyRentable: boolean;
    separateEntrance: boolean;
    legalLivingArea: boolean;
    roomHeightOk: boolean;
    rescueRouteOk: boolean;
    separateMetersOrAllocation: boolean;
    ownParking: boolean;
  };
};

export type PortfolioContext = {
  existingDepotMarketValue: number;
  existingDepotUnrealizedGain: number;
  existingDepotTaxBasis: number;
  existingDepotAfterTaxAtHorizon: number;
  strategyExistingDepotAfterTaxAtHorizon: number;
  alternativePathTotalWealthIncludingDepot: number;
  strategyPathTotalWealthIncludingDepot: number;
  wealthDeltaPercentOfBenchmarkTotal: number;
};

export type WealthDeltaContext = {
  horizonYears: number;
  benchmarkTotalWealthAtHorizon: number;
  nominalWealthDeltaAtHorizon: number;
  percentOfBenchmarkTotal: number;
  realWealthDeltaInTodayEur: number;
  monthlyEquivalentNominal: number;
  monthlyEquivalentInTodayEur: number;
};

export type RentalViewResult = {
  benchmark: 'etf' | 'tg';
  key: string;
  coldRentMonthly: number;
  effectiveRentMonthly: number;
  nonRecoverableCostsMonthly: number;
  maintenanceMonthly: number;
  netOperatingIncomeMonthly: number;
  taxableIncomeMonthly: number;
  incomeTaxMonthly: number;
  afterTaxCashFlowBeforeFinancingMonthly: number;
  afterTaxCashFlowAfterFinancingMonthly: number;
  monthlyPayment: number;
  initialLoan: number;
  equityInvested: number;
  liquidReserveAfterPurchase: number;
  rentWealthAtHorizon: number;
  rentalWealthAtHorizon: number;
  wealthDeltaAtHorizon: number;
  portfolioContext: PortfolioContext;
  wealthDeltaContext: WealthDeltaContext;
  maximumLeveragedPrice: number | null;
  leveragedWealthBreakEvenPrice: number | null;
  standaloneIntrinsicValue: number;
  objectValueIndex: number | null;
  strategyIndex: number | null;
  terminalPropertyEquity: number;
  remainingDebtAtHorizon: number;
  remainingDebtYear10: number;
  remainingDebtYear30: number;
  grossYieldOnTotalCost: number;
  netYieldOnTotalCost: number;
  decision: Decision;
};

export type MixedUseViewResult = {
  scenario: 'sq' | 'zm';
  benchmark: 'etf' | 'tg';
  key: string;
  selfUseYears: number;
  comparisonRent: number;
  coldRentMonthly: number;
  selfUsePhaseSavingsMonthly: number;
  rentalPhaseSavingsMonthly: number;
  monthlyPayment: number;
  initialLoan: number;
  equityInvested: number;
  liquidReserveAfterPurchase: number;
  rentWealthAtHorizon: number;
  mixedWealthAtHorizon: number;
  wealthDeltaAtHorizon: number;
  portfolioContext: PortfolioContext;
  wealthDeltaContext: WealthDeltaContext;
  maximumLeveragedPrice: number | null;
  leveragedWealthBreakEvenPrice: number | null;
  strategyIndex: number | null;
  terminalPropertyEquity: number;
  remainingDebtAtHorizon: number;
  remainingDebtYear5: number;
  remainingDebtYear10: number;
  remainingDebtYear30: number;
  decision: Decision;
};

export const fallbackDomainInput: DomainInput = {
  purchasePrice: 420000,
  purchaseCostRate: 8.57,
  currentRentSq: 1250,
  targetRentZm: 1250,
  ownerCostsMonthly: 500,
  recurringCapexMonthly: 150,
  rentalColdRentMonthly: 0,
  rentalNonRecoverableCostsMonthly: 0,
  rentalMaintenanceMonthly: 100,
  rentalVacancyRate: 2,
  rentalIncomeTaxRate: 42,
  allowNegativeRentalTaxOffset: false,
  rentalBuildingValueShare: 80,
  rentalDepreciationRate: 2,
  selfUseAreaM2: 0,
  rentalAreaM2: 0,
  netElwCashflowMonthly: 0,
  elwColdRentMonthly: 0,
  elwVacancyRate: 2,
  elwNonRecoverableCostsMonthly: 0,
  elwMaintenanceMonthly: 0,
  elwTaxRate: 0,
  elwLegallyRentable: true,
  elwLegalRentabilityHaircut: 0,
  separateEntrance: false,
  legalLivingArea: false,
  roomHeightOk: false,
  rescueRouteOk: false,
  separateMetersOrAllocation: false,
  ownParking: false,
  landValueShare: 20,
  buildingValueShare: 80,
  rentalBuildingShare: 0,
  rentalDebtInterestAllocationMethod: 'area',
  rentalDebtInterestShare: 0,
  rentalAllocatedLoanAmount: 0,
  livingAreaM2: 130,
  energyCertificateAreaM2: 106.9,
  currentHeatingCostMonthly: 236.2487102,
  currentCoolingKwhPerActiveMonth: 150,
  currentCoolingActiveMonths: 5,
  candidateEnergyDemandKwhM2a: 199.2,
  heatDemandKwhM2a: 0,
  heatPumpJaz: 3,
  auxiliaryElectricityKwhYear: 0,
  candidateEnergyPriceEurKwh: 0,
  candidateEnergySource: 'unknown',
  candidateCoolingProfile: 'similar',
  electricityPriceEurKwh: 0.35,
  pvBenefitMonthly: 0,
  pvPhases: [],
  energyCostGrowthRate: 3,
  co2PriceEurTon: 65,
  co2PriceGrowthRate: 3,
  etfReturnRate: 6,
  etfTaxRate: 18.46,
  inflationRate: 2,
  savingsReturnRate: 2,
  fcfGrowthRate: 2,
  rentGrowthRate: 2,
  ownerCostsGrowthRate: 2,
  recurringCapexGrowthRate: 2,
  savingsCapacityGrowthRate: 0,
  propertyAppreciationRate: 0,
  availableEquity: 200000,
  existingDepotMarketValue: 380000,
  existingDepotUnrealizedGain: 200000,
  minimumReserve: 70000,
  cashReserveTarget: 70000,
  cashReserveInitial: null,
  cashReserveReturnRate: null,
  loanInterestRate: 3.9,
  initialRepaymentRate: 1.5,
  comparisonInitialRepaymentRate: 1.08,
  financingRatio: null,
  financingRatioDefinition: 'purchase_price_ltv',
  maintainFinancingRatioDuringPriceSearch: true,
  existingDepotWithdrawalForPurchase: 0,
  savingsBeforeHousingMonthly: 5250,
  retirementYear: 0,
  postRetirementSavingsBeforeHousingMonthly: 0,
  minimumBuyPathSavingsMonthly: 4000,
  horizonYears: 30,
  immediateCapex: 0,
  futureCapex: 0,
  futureCapexYear: 0,
  futureCapexDurationYears: 1,
  capexValueEffectRate: 0,
  renovationPhases: [],
  poolAnnualCost: 0,
  poolFutureCapex: 0,
  poolFutureCapexYear: 0,
  saunaFutureCapex: 0,
  saunaFutureCapexYear: 0,
  pvBatteryReplacementYear: 0,
  pvBatteryReplacementCost: 0,
  inverterReplacementYear: 0,
  inverterReplacementCost: 0,
  heatPumpReplacementYear: 0,
  heatPumpReplacementCost: 0,
  ventilationReplacementYear: 0,
  ventilationReplacementCost: 0,
  kitchenFurnitureDepreciationMonthly: 0,
  sellingCostRate: 0,
  fixedRateYears: 0,
  refinanceInterestRate: 0,
  refinanceStressRate: 0,
  refinanceRepaymentRate: 0,
  mixedUseSelfUseYears: 0,
  searchMinPrice: 100000,
  searchMaxPrice: 1000000
};

export async function getDomainDefaults(signal?: AbortSignal): Promise<DomainInput> {
  const response = await fetch('/api/domain/defaults', { signal });

  if (!response.ok) {
    throw new Error(`Loading defaults failed with HTTP ${response.status}`);
  }

  return (await response.json()) as DomainInput;
}

export async function calculateDomain(
  input: DomainInput,
  signal?: AbortSignal
): Promise<DomainOutput> {
  const response = await fetch('/api/domain/calculate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
    signal
  });

  if (!response.ok) {
    throw new Error(`Calculation failed with HTTP ${response.status}`);
  }

  return (await response.json()) as DomainOutput;
}
